import { useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import { adrStore, generateAdrId } from './adrStore';
import type { ADR, AdrRelation, AdrRelationType, AdrCommentNode } from '../types';

export function useAdrs(): ADR[] {
  const cachedRef = useRef<ADR[]>([]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = adrStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const newAdrs = adrStore.getSnapshot().context.adrs;
    if (cachedRef.current.length === newAdrs.length &&
        cachedRef.current.every((a, i) => {
          const n = newAdrs[i];
          return n && a.id === n.id && a.updatedAt === n.updatedAt;
        })) {
      return cachedRef.current;
    }
    cachedRef.current = newAdrs;
    return newAdrs;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useAdrById(adrId: string): ADR | undefined {
  const adrs = useAdrs();
  return useMemo(() => adrs.find(a => a.id === adrId), [adrs, adrId]);
}

export function useAdrsForBoard(boardId: string): ADR[] {
  const adrs = useAdrs();
  return useMemo(() => adrs.filter(a =>
    a.scope.type === 'global' ||
    (a.scope.type === 'board' && a.scope.boardId === boardId) ||
    (a.scope.type === 'canvas' && a.scope.boardId === boardId)
  ), [adrs, boardId]);
}

export function useAdrsForCanvas(boardId: string, treeId: string): ADR[] {
  const adrs = useAdrs();
  return useMemo(() => adrs.filter(a =>
    a.scope.type === 'global' ||
    (a.scope.type === 'board' && a.scope.boardId === boardId) ||
    (a.scope.type === 'canvas' && a.scope.boardId === boardId && a.scope.treeId === treeId)
  ), [adrs, boardId, treeId]);
}

export function useAdrActions() {
  return useMemo(() => ({
    addAdr: (adr: ADR) =>
      adrStore.send({ type: 'adr.add', adr }),
    addNewAdr: (title: string, scope: ADR['scope']) => {
      const id = generateAdrId(title);
      const now = new Date().toISOString();
      const adr: ADR = {
        id, title, scope,
        context: [], decision: [], consequences: [],
        status: 'draft', implementationStatus: 'pending',
        relations: [], people: [], comments: [],
        createdAt: now, updatedAt: now,
      };
      adrStore.send({ type: 'adr.add', adr });
      return id;
    },
    updateAdr: (adrId: string, updates: Partial<Omit<ADR, 'id' | 'createdAt'>>) =>
      adrStore.send({ type: 'adr.update', adrId, updates }),
    deleteAdr: (adrId: string) =>
      adrStore.send({ type: 'adr.delete', adrId }),
    addRelation: (adrId: string, relation: AdrRelation) =>
      adrStore.send({ type: 'adr.addRelation', adrId, relation }),
    removeRelation: (adrId: string, targetAdrId: string, relationType: AdrRelationType) =>
      adrStore.send({ type: 'adr.removeRelation', adrId, targetAdrId, relationType }),
    addComment: (adrId: string, comment: AdrCommentNode, parentCommentId: string | null) =>
      adrStore.send({ type: 'adr.addComment', adrId, comment, parentCommentId }),
    resolveComment: (adrId: string, commentId: string) =>
      adrStore.send({ type: 'adr.resolveComment', adrId, commentId }),
    unresolveComment: (adrId: string, commentId: string) =>
      adrStore.send({ type: 'adr.unresolveComment', adrId, commentId }),
    resetAdrs: () =>
      adrStore.send({ type: 'adrs.reset' }),
    importAdrs: (adrs: ADR[]) =>
      adrStore.send({ type: 'adrs.import', adrs }),
  }), []);
}
